import React, { useEffect, useMemo } from "react";
import "./Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const TAGS = ["Plain-language terms", "Digital licenses only", "No hype claims"];

const Terms = () => {
  const { webinfo = {} } = useLocalContext();
  const brand = webinfo?.name || "Our Store";

  useEffect(() => {
    if (brand) {
      document.title = `Terms & Conditions | ${brand}`;
    }
  }, [brand]);

  const lastUpdated = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, []);

  const sections = [
    { id: "overview", title: "About These Terms" },
    { id: "eligibility", title: "Who May Use Our Services" },
    { id: "services", title: "What We Provide" },
    { id: "orders", title: "Orders, Pricing & Payments" },
    { id: "delivery", title: "Digital Delivery & Activation" },
    { id: "fair-use", title: "Fair Use & Restrictions" },
    { id: "your-responsibility", title: "Your Responsibilities" },
    { id: "privacy", title: "Privacy & Communication" },
    { id: "third-party", title: "Third-Party Software & Brands" },
    { id: "availability", title: "Availability & Changes" },
    { id: "liability", title: "Disclaimers & Liability Limits" },
    { id: "refunds", title: "Refunds, Cancellations & Disputes" },
    { id: "updates", title: "Updates to These Terms" },
    { id: "law", title: "Governing Law & Disputes" },
    { id: "contact", title: "How to Contact Us" },
  ];

  // Best-effort website URL
  let siteUrl = "";
  if (typeof window !== "undefined") {
    siteUrl = window.location.origin;
  }

  return (
    <div className="t-page">
      {/* HERO */}
      <header className="t-hero" aria-labelledby="terms-heading">
        <div className="t-hero__top">
          <p className="t-hero__eyebrow">Legal information</p>
          <h1 id="terms-heading" className="t-hero__title">
            Terms &amp; Conditions
          </h1>
          <p className="t-hero__subtitle">
            These Terms &amp; Conditions explain how{" "}
            <strong>{brand}</strong> operates this website, sells digital
            products, and provides related support. Please read them carefully
            before placing an order or using our services.
          </p>

          <div className="t-hero__meta">
            Last updated: <strong>{lastUpdated}</strong>
          </div>

          <div className="t-hero__tags" aria-label="Key principles">
            {TAGS.map((tag) => (
              <span key={tag} className="t-pill">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Quick summary tiles */}
        <div
          className="t-hero__highlights"
          aria-label="Short summary of these terms"
        >
          <div className="t-hero__highlight">
            <span className="t-chip">Digital licences only</span>
            <p>
              We sell <strong>digital licenses and related support</strong> only.
              No boxes or CDs&mdash;delivery is online after payment.
            </p>
          </div>
          <div className="t-hero__highlight">
            <span className="t-chip">Vendor rules apply</span>
            <p>
              Software features, limits and behaviour always follow the{" "}
              <strong>original vendor&apos;s terms (EULA)</strong>, not ours.
            </p>
          </div>
          <div className="t-hero__highlight">
            <span className="t-chip">Refunds are limited</span>
            <p>
              Once a key is issued, <strong>refund options are limited</strong>{" "}
              and follow our{" "}
              <a href="/refund-policy">Refund &amp; Return Policy</a>.
            </p>
          </div>
        </div>
      </header>

      {/* MAIN GRID */}
      <section className="t-grid" aria-label="Terms content layout">
        {/* TOC */}
        <aside className="t-toc" aria-label="On this page">
          <div className="t-card t-toc__card">
            <div className="t-toc__title">On this page</div>
            <nav>
              <ol className="t-toc__list">
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span className="t-toc__num">{index + 1}.</span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </aside>

        {/* CONTENT */}
        <article className="t-card t-content" role="article">
          {/* 1 */}
          <section id="overview" className="t-sec">
            <h2 className="t-h2">About These Terms</h2>
            <p>
              These Terms &amp; Conditions (“Terms”) govern your use of this
              website and any digital products or services offered by{" "}
              <strong>{brand}</strong> (together, the “Services”).
            </p>
            <p>
              By browsing the website, placing an order, or contacting us for
              support, you agree to these Terms as well as our{" "}
              <a href="/privacy-policy">Privacy Policy</a> and{" "}
              <a href="/refund-policy">Refund &amp; Return Policy</a>. If you do
              not agree, you should not use the Services.
            </p>
          </section>

          {/* 2 */}
          <section id="eligibility" className="t-sec">
            <h2 className="t-h2">Who May Use Our Services</h2>
            <ul className="t-list">
              <li>
                You must be legally able to enter into a binding contract in
                your country or region.
              </li>
              <li>
                If you use the Services on behalf of an organization, you
                confirm that you are authorized to accept these Terms on its
                behalf.
              </li>
              <li>
                You are responsible for keeping any account details, passwords,
                and contact information accurate and secure.
              </li>
              <li>
                If you become aware of unauthorized use of your account or
                order, you should notify us as soon as reasonably possible.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section id="services" className="t-sec">
            <h2 className="t-h2">What We Provide</h2>
            <p>
              We focus on providing <strong>digital products</strong> (such as
              security suites, productivity tools or similar) in the form of{" "}
              <strong>license keys / activation codes</strong>, along with basic
              assistance related to orders and activation.
            </p>
            <ul className="t-list">
              <li>
                We do not ship physical items. All products are delivered
                digitally where available.
              </li>
              <li>
                Technical features, device limits, duration, and system
                requirements are defined by the original software vendor, not by{" "}
                {brand}.
              </li>
              <li>
                We do not modify or crack software, and we do not encourage or
                support piracy.
              </li>
              <li>
                Any add-ons or bonuses (for example, extra guidance materials)
                are provided on a best-effort basis and may change or end.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section id="orders" className="t-sec">
            <h2 className="t-h2">Orders, Pricing &amp; Payments</h2>
            <ul className="t-list">
              <li>
                Product prices and offers may be updated from time to time. The
                price confirmed at checkout applies to that order.
              </li>
              <li>
                You agree to provide accurate billing and contact details so we
                can deliver your license and support you.
              </li>
              <li>
                Any taxes, currency conversion fees, or payment gateway charges
                are your responsibility and may appear separately from the base
                product price.
              </li>
              <li>
                If an order involves suspected fraud, misuse, or chargebacks, we
                may cancel or suspend associated licenses where legally
                permitted.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section id="delivery" className="t-sec">
            <h2 className="t-h2">Digital Delivery &amp; Activation</h2>
            <p>
              As our products are digital, license keys or activation
              instructions are typically delivered by email, on-screen, or via
              your order details after payment is confirmed.
            </p>
            <ul className="t-list">
              <li>
                Delivery timelines may vary depending on payment checks, stock
                of license types, or manual review where required.
              </li>
              <li>
                You are responsible for following the activation instructions
                provided and for using the license on compatible devices and
                operating systems.
              </li>
              <li>
                We cannot be held responsible for activation issues caused by
                unsupported hardware, outdated operating systems, regional
                restrictions, or vendor policy changes.
              </li>
              <li>
                Once a license key is exposed to you, it is treated as “used”
                and cannot be safely resold to others, which is why refund
                options are limited.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section id="fair-use" className="t-sec">
            <h2 className="t-h2">Fair Use &amp; Restrictions</h2>
            <ul className="t-list">
              <li>
                You must not attempt to duplicate, resell, or share license keys
                in ways that violate vendor terms or applicable law.
              </li>
              <li>
                You must not use stolen payment methods, false identities, or
                misleading contact details when placing orders.
              </li>
              <li>
                You must not use the Services to distribute malware, phishing,
                or other harmful or unlawful content.
              </li>
              <li>
                We may suspend or cancel access to Services where we reasonably
                believe there is abuse, fraud, or breach of these Terms.
              </li>
            </ul>
          </section>

          {/* 7 */}
          <section id="your-responsibility" className="t-sec">
            <h2 className="t-h2">Your Responsibilities</h2>
            <ul className="t-list">
              <li>
                Keep copies of your order confirmation and license details for
                your own records wherever possible.
              </li>
              <li>
                Back up your important data before installing new software or
                changing system settings. We cannot guarantee that issues,
                conflicts, or data loss will never occur.
              </li>
              <li>
                Any reviews, feedback, or support messages you send should be
                accurate, respectful, and lawful.
              </li>
              <li>
                You allow us to use non-sensitive feedback (such as reviews)
                solely for running and improving our Services (for example,
                display on product pages).
              </li>
            </ul>
          </section>

          {/* 8 */}
          <section id="privacy" className="t-sec">
            <h2 className="t-h2">Privacy &amp; Communication</h2>
            <p>
              We handle your personal data according to our{" "}
              <a href="/privacy-policy">Privacy Policy</a>. That policy explains
              what we collect, why we collect it, and how you can manage your
              choices.
            </p>
            <p>
              By using the Services, you agree that we may contact you with
              service-related messages, such as order confirmations, security
              notices, and support replies. Where required, we will request your
              consent before sending marketing messages, and you can usually opt
              out at any time.
            </p>
          </section>

          {/* 9 */}
          <section id="third-party" className="t-sec">
            <h2 className="t-h2">Third-Party Software &amp; Brands</h2>
            <p>
              Many of the products we offer are created and owned by{" "}
              <strong>third-party software vendors</strong>. Their products are
              governed by their own End User License Agreements (EULAs), privacy
              policies, and terms.
            </p>
            <ul className="t-list">
              <li>
                We do not claim ownership of third-party brands, logos, or
                product names. These remain the property of their respective
                owners and are used for identification only.
              </li>
              <li>
                Vendors may change features, pricing, or availability of their
                products. These decisions are outside our control.
              </li>
              <li>
                In some situations, you may need to contact the vendor directly
                for technical troubleshooting or account issues on their
                platform.
              </li>
            </ul>
          </section>

          {/* 10 */}
          <section id="availability" className="t-sec">
            <h2 className="t-h2">Availability &amp; Changes</h2>
            <p>
              We aim to keep the website and Services accurate and available,
              but we cannot guarantee continuous, error-free operation at all
              times.
            </p>
            <p>
              Maintenance, security updates, hosting problems, or events beyond
              our control (such as network outages or infrastructure failures)
              may temporarily affect access. We may also change, pause, or stop
              offering certain products or features, while honoring legal
              obligations for existing orders.
            </p>
          </section>

          {/* 11 */}
          <section id="liability" className="t-sec">
            <h2 className="t-h2">Disclaimers &amp; Liability Limits</h2>
            <p>
              The Services are provided on an <em>“as is” and “as available”</em>{" "}
              basis. To the fullest extent permitted by law, we do not provide
              guarantees that the Services will meet every need, work on every
              device, or stop every possible threat.
            </p>
            <p>
              To the maximum extent allowed by applicable law,{" "}
              <strong>{brand}</strong> and its owners, team members, and
              partners will not be responsible for indirect, incidental, or
              consequential losses (such as loss of data, profits, or business)
              that arise from using or being unable to use the Services.
            </p>
            <p>
              Where our liability cannot be fully excluded, it is limited to the
              amount you actually paid to us for the order that gave rise to the
              issue, within the <strong>three (3) months</strong> before the
              event.
            </p>
          </section>

          {/* 12 */}
          <section id="refunds" className="t-sec">
            <h2 className="t-h2">Refunds, Cancellations &amp; Disputes</h2>
            <p>
              Because digital license keys cannot be “returned” after they are
              revealed to you, <strong>most completed orders are final</strong>,
              subject to the situations described in our{" "}
              <a href="/refund-policy">Refund &amp; Return Policy</a>.
            </p>
            <ul className="t-list">
              <li>
                We may review requests for refund or replacement where there is
                clear evidence of a defective key, duplicate payment, or an
                error directly caused by us.
              </li>
              <li>
                You may be asked for information such as screenshots, error
                messages, or vendor responses to help us investigate.
              </li>
              <li>
                We encourage you to contact us first to try to resolve issues
                before starting chargebacks or external disputes, where
                reasonable and permitted by your payment provider.
              </li>
            </ul>
          </section>

          {/* 13 */}
          <section id="updates" className="t-sec">
            <h2 className="t-h2">Updates to These Terms</h2>
            <p>
              We may update these Terms from time to time—for example, to
              reflect changes in law, vendor policies, or how the Services work.
            </p>
            <p>
              When we make meaningful changes, we will adjust the “Last updated”
              date at the top of this page. Your continued use of the Services
              after changes take effect means you accept the updated Terms. If
              you do not agree, you should stop using the Services.
            </p>
          </section>

          {/* 14 */}
          <section id="law" className="t-sec">
            <h2 className="t-h2">Governing Law &amp; Disputes</h2>
            <p>
              Unless local consumer protection rules require otherwise, these
              Terms are governed by the laws of the jurisdiction where{" "}
              <strong>{brand}</strong> is established or primarily operates,
              without regard to conflict-of-law principles.
            </p>
            <p>
              We aim to resolve concerns through clear, good-faith discussion
              first. If a dispute cannot be resolved informally, it will be
              handled by the competent courts of that jurisdiction, unless
              applicable law gives you a different mandatory forum.
            </p>
          </section>

          {/* 15 */}
          <section id="contact" className="t-sec">
            <h2 className="t-h2">How to Contact Us</h2>
            <p>
              If you have questions about these Terms or about a specific order,
              you can contact us using the details below.
            </p>
            <address className="t-address">
              <strong>{brand}</strong>
              <br />
              {webinfo.address && (
                <>
                  {webinfo.address}
                  <br />
                </>
              )}
              {webinfo.email && (
                <>
                  Email:{" "}
                  <a href={`mailto:${webinfo.email}`}>{webinfo.email}</a>
                  <br />
                </>
              )}
              {webinfo.phone && (
                <>
                  Phone: <a href={`tel:${webinfo.phone}`}>{webinfo.phone}</a>
                  <br />
                </>
              )}
              {siteUrl && (
                <>
                  Website:{" "}
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                    {siteUrl}
                  </a>
                </>
              )}
            </address>
          </section>
        </article>
      </section>
    </div>
  );
};

export default Terms;
